import { t } from "@/languages/i18n"
import { normalizeUrl } from "@/utils/ensure-protocol-helper"
import { PlusOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Checkbox, Divider, Empty, Form, Input, Segmented, Select, Spin, Tag, Tooltip } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import type { MenuSourceItem, MenuSourceType } from "../types/menu"

interface MenuSourcePanelProps {
  sources: MenuSourceItem[]
  loading: boolean
  disabled?: boolean
  /** Ngôn ngữ của menu đang sửa — dùng làm bộ lọc mặc định cho danh sách nguồn. */
  menuLocale: string
  onAdd: (items: { name: string; slug: string; url: string }[]) => void
}

const TYPE_LABEL: Record<MenuSourceType, string> = {
  page: t("Trang"),
  post: t("Bài viết"),
  category: t("Danh mục"),
  "product-category": t("Danh mục SP"),
  custom: t("Link"),
}

const TYPE_COLOR: Record<MenuSourceType, string> = {
  page: "blue",
  post: "green",
  category: "gold",
  "product-category": "purple",
  custom: "default",
}

/**
 * Panel "Thêm mục" — thay 4 accordion loại trừ nhau của màn cũ bằng một danh
 * sách có tìm kiếm và bộ lọc theo loại. Trạng thái tick giữ theo `key`
 * (`<loại>:<slug>`) chứ không theo tên, nên hai trang trùng tên không lẫn nhau.
 */
const MenuSourcePanel: React.FC<MenuSourcePanelProps> = ({ sources, loading, disabled, menuLocale, onAdd }) => {
  const [keyword, setKeyword] = useState("")
  const [type, setType] = useState<MenuSourceType | "all">("all")
  const [localeFilter, setLocaleFilter] = useState(menuLocale)
  const [checked, setChecked] = useState<string[]>([])
  const [customForm] = Form.useForm()

  // Menu đổi ngôn ngữ (lúc tạo mới) thì bộ lọc bám theo.
  useEffect(() => {
    setLocaleFilter(menuLocale)
  }, [menuLocale])

  const visibleSources = useMemo(() => {
    const term = keyword.trim().toLowerCase()
    return sources.filter(item => {
      if (type !== "all" && item.type !== type) return false
      if (localeFilter !== "all" && item.locale && item.locale !== localeFilter) return false
      if (!term) return true
      return item.name.toLowerCase().includes(term) || item.slug.toLowerCase().includes(term)
    })
  }, [keyword, localeFilter, sources, type])

  const visibleKeys = useMemo(() => visibleSources.map(item => item.key), [visibleSources])
  const allVisibleChecked = visibleKeys.length > 0 && visibleKeys.every(key => checked.includes(key))

  const toggleSelectAll = () => {
    setChecked(current =>
      allVisibleChecked
        ? current.filter(key => !visibleKeys.includes(key))
        : Array.from(new Set([...current, ...visibleKeys])),
    )
  }

  const handleAddChecked = () => {
    const picked = sources.filter(item => checked.includes(item.key))
    if (picked.length === 0) return

    onAdd(picked.map(item => ({ name: item.name, slug: item.slug, url: item.slug })))
    setChecked([])
  }

  const handleAddCustom = async () => {
    const values = await customForm.validateFields()
    const url = normalizeUrl(values.url?.trim())
    onAdd([{ name: values.label.trim(), slug: url, url }])
    customForm.resetFields()
  }

  return (
    <div className="flex flex-col gap-3">
      <Input
        allowClear
        value={keyword}
        onChange={event => setKeyword(event.target.value)}
        placeholder={t("Tìm trang, bài viết, danh mục…")}
        prefix={<SearchOutlined className="text-gray-400" />}
      />

      <Segmented
        block
        size="small"
        value={type}
        onChange={value => setType(value as MenuSourceType | "all")}
        options={[
          { label: t("Tất cả"), value: "all" },
          { label: TYPE_LABEL.page, value: "page" },
          { label: TYPE_LABEL.post, value: "post" },
          { label: TYPE_LABEL.category, value: "category" },
        ]}
      />

      <Select
        size="small"
        value={localeFilter}
        onChange={setLocaleFilter}
        options={[
          { value: "all", label: t("Mọi ngôn ngữ") },
          { value: "vi", label: t("Tiếng Việt") },
          { value: "en", label: t("Tiếng Anh") },
          { value: "cs", label: t("Tiếng Séc") },
        ]}
      />

      <div className="border border-gray-200 rounded-md h-[260px] overflow-y-auto p-2">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Spin />
          </div>
        ) : visibleSources.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("Không tìm thấy nội dung nào")} />
        ) : (
          <Checkbox.Group value={checked} onChange={values => setChecked(values as string[])} className="w-full">
            <div className="flex flex-col gap-1 w-full">
              {visibleSources.map(item => (
                <Checkbox key={item.key} value={item.key} className="w-full !ml-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <Tooltip title={item.slug}>
                      <span className="truncate max-w-[170px]">{item.name}</span>
                    </Tooltip>
                    <Tag color={TYPE_COLOR[item.type]} className="!mr-0 shrink-0">
                      {TYPE_LABEL[item.type]}
                    </Tag>
                    {localeFilter === "all" && item.locale && (
                      <Tag className="!mr-0 shrink-0 uppercase">{item.locale}</Tag>
                    )}
                  </div>
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
        )}
      </div>

      <div className="flex items-center justify-between">
        <Checkbox checked={allVisibleChecked} disabled={visibleKeys.length === 0} onChange={toggleSelectAll}>
          {t("Chọn tất cả")}
        </Checkbox>
        <span className="text-gray-500 text-xs">
          {checked.length > 0 ? t("Đã chọn {count}").replace("{count}", String(checked.length)) : ""}
        </span>
      </div>

      <Button
        type="primary"
        icon={<PlusOutlined />}
        block
        disabled={disabled || checked.length === 0}
        onClick={handleAddChecked}
      >
        {t("Thêm vào menu")}
      </Button>

      <Divider className="!my-1" plain>
        <span className="text-xs text-gray-500">{t("Link tùy chỉnh")}</span>
      </Divider>

      <Form form={customForm} layout="vertical" size="small" disabled={disabled}>
        <Form.Item
          name="label"
          label={t("Nhãn hiển thị")}
          rules={[{ required: true, message: t("Nhập nhãn hiển thị") }]}
          className="!mb-2"
        >
          <Input placeholder={t("Ví dụ: Tuyển dụng")} />
        </Form.Item>
        <Form.Item
          name="url"
          label={t("Đường dẫn")}
          rules={[{ required: true, message: t("Nhập đường dẫn") }]}
          className="!mb-2"
        >
          <Input placeholder="https://…" />
        </Form.Item>
        <Button icon={<PlusOutlined />} block onClick={handleAddCustom} disabled={disabled}>
          {t("Thêm link")}
        </Button>
      </Form>
    </div>
  )
}

export default MenuSourcePanel
